<!--
  ONE PERSON — `/person/Q23547`, or `/person/Bong%20Joon-ho` when the link's
  builder only had a name. The owner's ask, both halves: click a cast face and
  "it would open their profile … their picture and some information about
  them"; click a director's name and get "the director's movies with the short
  profile of the director at the top".

  ONE PAGE, THREE RAILS, FIXED ORDER (the owner, 2026-08-17): "Movies" (acted
  in), "Series" (acted in), "Directed" (P57, both kinds pooled). Always these,
  always this order — and a category with nothing in it renders NOTHING: no
  heading, no ghost row. An actor who never directed gets two rails; a
  director who never acted gets one. The header above them is the same for
  everybody: the portrait, the name, the life line, Wikipedia's first
  paragraph. Data sourcing, etiquette, the wrong-person gates and the
  three-way split live in `lib/ui/person.ts`; this screen draws what it is
  handed and paces the catalogue.

  THE RAILS ARE THE HOUSE RAILS. `Rail` + `Poster`, so the page inherits the
  full tile experience — focus ring, details strip, tap semantics, endless
  loading and the loop at exhaustion, TV d-pad — without owning any of it.
  The acted rails are single-kind by construction so their tiles drop the
  kind tag (the house rule); Directed pools films and shows, so its tiles
  keep it.

  EXHAUSTIVE, PROGRESSIVELY. Each rail holds the person's ENTIRE Wikidata list
  for its category, resolved against the catalogue as the viewer approaches
  the end — through the rail's own `more()` contract now: Rail asks when its
  scroll nears its end, this page verifies the next stretch of credits (four
  requests in flight at most, shared across all three rails) and confirmed
  cards land in fame order. A dead id — one the catalogue cleanly answered
  dead — is dropped, never drawn; an id whose lookup merely ERRORED is
  parked and retried, because a network blip is not a fact about a credit.
  Only when Wikidata's list is spent on clean answers does the rail say
  'end' and loop back to its beginning like every rail in the app. A rail's heading is HELD BACK until
  its first confirmed member, which is how "no empty rows" holds even for a
  category that only turns out empty after verification — it never painted
  anything to take away.

  FAIL-SOFT IS THE WHOLE DESIGN. No Wikipedia page = no bio paragraph, the
  rest stands. No Wikidata entity at all = the name, a plate, and a sentence —
  never a broken page. Attribution: the bio is CC BY-SA text so "From
  Wikipedia" links the article; the portrait credit links its source page
  (Commons file page or TVMaze person page) — the credit trail that used to
  ride the cast bubbles' title text lands here, visible.
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import Empty from '$lib/ui/Empty.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import Rail from '$lib/ui/Rail.svelte';
  import Skeleton from '$lib/ui/Skeleton.svelte';
  import Art from '$lib/ui/Art.svelte';
  import { definiteMiss, detail } from '$lib/ui/cinemeta';
  import { MORE_RETRY_MS } from '$lib/ui/rail-ring';
  import { nav } from '$lib/ui/nav.svelte';
  import { loadPerson, type PersonCredit, type PersonPage } from '$lib/ui/person';

  const key = $derived(nav.route.name === 'person' ? nav.route.key : '');

  type RailId = 'movies' | 'series' | 'directed';

  /** One rail's whole life: the full Wikidata list, the confirmed cards, and
   *  how far into the list verification has read. */
  interface RailRow {
    id: RailId;
    label: string;
    /** The person's ENTIRE list for this category, fame-ordered. */
    credits: PersonCredit[];
    /** Catalogue-confirmed cards, still in fame order. */
    items: CatalogItem[];
    /** Next credit to verify. `cursor − items.length` credits died on the way. */
    cursor: number;
    /**
     * Credits whose lookups ERRORED — network, 5xx — as opposed to the
     * catalogue cleanly answering "no such title". AN ERRORED CREDIT IS NOT
     * A DEAD ONE: these wait for a later stretch (each walk retries them at
     * most once), and while any wait, the list is not spent — `done` may
     * only be concluded from clean answers.
     */
    parked: PersonCredit[];
    /** The whole list has been CLEANLY read; the rail's `more()` answers 'end'. */
    done: boolean;
  }

  let page = $state<PersonPage | null>(null);
  let failed = $state(false);
  let rails = $state<RailRow[]>([]);

  /** The opening stretch: confirmed cards past the widest screen's row (a
   *  1920px desktop shows ~9, a television ~7), so the first paint scrolls. */
  const FIRST = 12;
  /** One approach's growth — same politeness as the opening stretch. */
  const STEP = 12;
  /** Collection.svelte's politeness, kept: at most four catalogue requests in
   *  flight, SHARED across the three rails, however the sentinels race. */
  const LANES = 4;

  /** The current page's abort — module-shaped state, not view state. */
  let live: AbortController | null = null;

  $effect(() => {
    const k = key;
    page = null;
    failed = false;
    rails = [];
    chains.clear();
    if (!k) return;

    const abort = new AbortController();
    live = abort;

    void loadPerson(k, abort.signal)
      .then((answer) => {
        if (abort.signal.aborted) return;
        page = answer;
        const bill: { id: RailId; label: string; credits: PersonCredit[] }[] = [
          { id: 'movies', label: 'Movies', credits: answer.filmography.movies },
          { id: 'series', label: 'Series', credits: answer.filmography.series },
          { id: 'directed', label: 'Directed', credits: answer.filmography.directed }
        ];
        // A category Wikidata already knows is empty never becomes a rail at
        // all — the known-empty half of "no empty rows".
        rails = bill
          .filter((r) => r.credits.length > 0)
          .map((r) => ({ ...r, items: [], cursor: 0, parked: [], done: false }));
        for (const rail of rails) void fill(rail, FIRST).then(() => refillLater(rail));
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        failed = true;
      });

    return () => {
      abort.abort();
      for (const t of refillTimers) clearTimeout(t);
      refillTimers.clear();
    };
  });

  /* ── The starved opening fill retries itself ─────────────────────────────
   * A rail that confirmed NOTHING because its lookups ERRORED (never because
   * the catalogue counted its credits out) has no mounted Rail yet — the
   * heading is held back until the first confirmed member — so nothing would
   * ever ask again. It retries here instead, on the same cooldown a mounted
   * rail's rejected `more()` gets; a rail with even one card retries through
   * `more()` like everywhere else.
   */
  const refillTimers = new Set<ReturnType<typeof setTimeout>>();
  function refillLater(rail: RailRow): void {
    if (rail.done || rail.items.length > 0) return;
    const signal = live?.signal;
    if (!signal || signal.aborted) return;
    const t = setTimeout(() => {
      refillTimers.delete(t);
      void fill(rail, FIRST).then(() => refillLater(rail));
    }, MORE_RETRY_MS);
    refillTimers.add(t);
  }

  /* ── The shared four-lane budget ─────────────────────────────────────────
   * Every catalogue read on this page goes through here, so three rails
   * filling at once still hold the old grid's ceiling: four in flight, ever. */
  let lanes = 0;
  const laneQueue: (() => void)[] = [];
  async function lane<T>(job: () => Promise<T>): Promise<T> {
    if (lanes >= LANES) await new Promise<void>((open) => laneQueue.push(open));
    lanes += 1;
    try {
      return await job();
    } finally {
      lanes -= 1;
      laneQueue.shift()?.();
    }
  }

  /* ── Filling a rail ──────────────────────────────────────────────────────
   * Serialized per rail (the chain), because two signals can ask for the same
   * stretch — the opening fill and an eager sentinel — and interleaved fills
   * would double-read the cursor. The chain makes the second ask wait its
   * turn; `doFill` re-checks `done` when that turn comes.
   */
  const chains = new Map<RailId, Promise<void>>();
  function fill(rail: RailRow, want: number): Promise<void> {
    const next = (chains.get(rail.id) ?? Promise.resolve()).then(() => doFill(rail, want));
    chains.set(rail.id, next);
    return next;
  }

  /**
   * Verify credits until `want` more cards are CONFIRMED or the list runs
   * out — a dead id costs a read but not a card, so a rail full of ghosts of
   * the past keeps reading until it finds the living or proves there are
   * none. Batches of four await together and land together, in list order,
   * so fame order survives however the answers race back.
   *
   * A dead id is one the catalogue ANSWERED dead (a clean 404). A lookup
   * that merely ERRORED is parked on the rail instead — retried at most once
   * per walk, behind the fresh credits, so a dead network cannot spin this
   * loop — and `done` below is only ever concluded once every credit has
   * answered cleanly.
   */
  async function doFill(rail: RailRow, want: number): Promise<void> {
    const signal = live?.signal;
    if (!signal || signal.aborted || rail.done) return;
    const second = rail.parked;
    rail.parked = [];
    let added = 0;
    while (
      added < want &&
      (rail.cursor < rail.credits.length || second.length > 0) &&
      !signal.aborted
    ) {
      const batch: PersonCredit[] = [];
      while (batch.length < LANES && rail.cursor < rail.credits.length) {
        batch.push(rail.credits[rail.cursor]!);
        rail.cursor += 1;
      }
      while (batch.length < LANES && second.length > 0) batch.push(second.shift()!);
      const answers = await Promise.all(
        batch.map((credit) =>
          lane(() => detail(credit.kind, credit.imdbId, signal)).catch((err: unknown) => {
            /* A clean 404 counts the credit out; anything else — including
               this page's own abort — parks it for a later walk. */
            if (!definiteMiss(err)) rail.parked.push(credit);
            return null;
          })
        )
      );
      if (signal.aborted) break;
      for (const found of answers) {
        if (found) {
          rail.items.push(found);
          added += 1;
        }
      }
    }
    /* Second chances this walk never reached stay parked for the next one. */
    rail.parked.push(...second);
    if (signal.aborted) return;
    if (rail.cursor >= rail.credits.length && rail.parked.length === 0) rail.done = true;
  }

  /* ── The rail drives the depth ───────────────────────────────────────────
   * The old ghost-card sentinel is gone: `Rail` itself asks, through its
   * `more()` contract, whenever its scroll nears its end — and it windows
   * the DOM, so a two-hundred-credit filmography never renders two hundred
   * tiles at once. This page's whole job per approach is one stretch of
   * verification; 'end' once Wikidata's list is spent, after which the rail
   * loops. The per-rail chain still serializes the opening fill against an
   * eager approach, so the cursor is never double-read.
   */
  function moreFor(rail: RailRow): () => Promise<'grew' | 'end'> {
    return async () => {
      if (rail.done) return 'end';
      const before = rail.items.length;
      await fill(rail, STEP);
      if (rail.items.length > before) return 'grew';
      if (rail.done) return 'end';
      /* Nothing confirmed and the list is NOT cleanly spent: the stretch
         errored (credits are parked). Thrown, so Rail treats it as transient
         — ghost, cooldown, retry — never as this person's last word. */
      throw new Error('the stretch errored; the list is not spent');
    };
  }

  const profile = $derived(page?.profile ?? null);

  /** "1956" · "1967–2014" — en dash, no "born" boilerplate. */
  const life = $derived(
    profile?.born ? (profile.died ? `${profile.born}–${profile.died}` : String(profile.born)) : ''
  );

  /** Only when every rail has read its whole list and none found a living
   *  card may the page say the catalogue holds nothing — a rail still
   *  reading might yet produce one. Vacuously true when Wikidata listed
   *  nothing usable, which is the same sentence. */
  const barren = $derived(
    page !== null && rails.every((r) => r.done) && !rails.some((r) => r.items.length > 0)
  );
</script>

<svelte:head>
  <title>{profile ? `${profile.name} — Jovii` : 'Person — Jovii'}</title>
</svelte:head>

{#if !page && !failed}
  <!-- The header's exact bones, held by skeletons so nothing jumps. The rails
       hold themselves back until their first confirmed member (see the header
       comment), so nothing below the header shimmers speculatively. -->
  <header class="head" aria-busy="true">
    <div class="portrait"><Skeleton ratio="3 / 4" /></div>
    <div class="who">
      <Skeleton width="14ch" height="1.6rem" radius="6px" />
      <Skeleton width="22ch" height="0.85rem" radius="5px" />
      <Skeleton width="100%" height="0.85rem" radius="5px" />
      <Skeleton width="82%" height="0.85rem" radius="5px" />
    </div>
  </header>
{:else if failed}
  <div class="pad">
    <Empty
      icon="alert"
      tone="bad"
      title="This profile cannot be drawn"
      detail="The public sources this page reads did not answer. Try again in a moment."
    />
  </div>
{:else if profile}
  <header class="head">
    <div class="portrait">
      <!-- Art keeps the initials plate underneath: a person with no public
           photo reads as a person, never a broken box. -->
      <Art src={profile.photo} alt="" label={profile.name} ratio="3 / 4" eager />
    </div>
    <div class="who">
      <h1>{profile.name}</h1>
      {#if life || profile.description}
        <p class="line">
          {#if life}<span class="years">{life}</span>{/if}
          {#if life && profile.description}<span class="dot" aria-hidden="true">·</span>{/if}
          {#if profile.description}{profile.description}{/if}
        </p>
      {/if}
    </div>
    {#if profile.bio}
      <p class="bio">{profile.bio}</p>
    {/if}
    {#if profile.wikiUrl || profile.photoCredit}
      <p class="credits">
        {#if profile.wikiUrl}
          <a href={profile.wikiUrl} target="_blank" rel="noopener noreferrer">From Wikipedia</a>
        {/if}
        {#if profile.photoCredit}
          <a href={profile.photoCredit.url} target="_blank" rel="noopener noreferrer">{profile.photoCredit.label}</a>
        {/if}
      </p>
    {/if}
  </header>

  {#if !profile.qid}
    <div class="pad">
      <Empty
        icon="user"
        title="No public profile found"
        detail="Nobody on the public record matches this name closely enough to trust. Their work still plays from the title pages that name them."
      />
    </div>
  {:else}
    <div class="rails">
      {#each rails as rail (rail.id)}
        <!-- HELD BACK until the first confirmed member: a rail that would
             turn out empty never paints a heading to take away. -->
        {#if rail.items.length > 0}
          <Rail title={rail.label} items={rail.items} more={moreFor(rail)}>
            {#snippet tile(item: CatalogItem, ordinal: number)}
              <!-- Acted rails are single-kind by construction, so no per-tile
                   kind tag; Directed pools kinds, so its tiles keep it. -->
              <Poster {item} eager={ordinal <= 4} tagKind={rail.id === 'directed'} />
            {/snippet}
          </Rail>
        {/if}
      {/each}
      {#if barren}
        <div class="pad">
          <Empty
            icon="film"
            title="Nothing of theirs is in the catalogue"
            detail="The public record lists their work, but none of it resolves against the catalogue this app draws from."
          />
        </div>
      {/if}
    </div>
  {/if}
{/if}

<style>
  .pad {
    padding: var(--space-5) var(--page-pad);
  }

  /*
   * Like Collection: no hero, so the page itself pays the chrome offset.
   *
   * The header is a two-column grid — the portrait in the first column, the
   * name/line/bio/credits stacked in the second — so on a wide screen the
   * biography reads BESIDE the face at a 62ch measure. On a phone the bio and
   * credit links drop to a full-width row under both (grid-column 1/-1)
   * rather than squeezing into the sliver beside the portrait; the portrait
   * itself narrows with the viewport instead of stacking, because a face plus
   * a name IS the header and pushing the name below the fold serves nobody.
   */
  .head {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: var(--space-3) var(--space-5);
    align-items: start;
    padding: calc(var(--chrome-overlap) + var(--space-6)) var(--page-pad) var(--space-2);
  }

  .portrait {
    grid-row: 1 / span 3;
    width: clamp(108px, 26vw, 176px);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .who {
    min-width: 0;
    display: grid;
    gap: var(--space-2);
    align-content: start;
  }

  h1 {
    font-size: var(--text-2xl);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    line-height: 1.1;
  }

  .line {
    color: var(--text-dim);
    font-size: var(--text-sm);
  }
  .years {
    font-variant-numeric: tabular-nums;
  }
  .dot {
    margin: 0 0.45em;
    color: var(--text-faint);
  }

  .bio {
    max-width: 62ch;
    color: var(--text-dim);
    font-size: var(--text-sm);
    line-height: 1.55;
  }

  .credits {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-4);
    font-size: var(--text-xs);
  }
  .credits a {
    color: var(--text-faint);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .credits a:hover {
    color: var(--text);
  }

  /*
   * The rails own their horizontal manners (full-bleed track, page-pad
   * heading — Rail.svelte); this wrapper only pays the vertical rhythm
   * between the header above and the footer below. --space-4 on top rather
   * than -2: the first rail's heading follows the focused-tile headroom rule
   * (Rail's own `padding-block: 20px` on the track), so the band under the
   * credits line reads right without a second rule here.
   */
  .rails {
    padding-block: var(--space-4) var(--space-6);
  }

  @media (max-width: 560px) {
    .bio,
    .credits {
      grid-column: 1 / -1;
    }
    .portrait {
      grid-row: 1;
    }
  }
</style>
